import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { isDemoCaptureEnabled } from "./is-demo-capture-enabled";
import { pickCaptureDirectory } from "./demo-capture.utils";
import {
  runCaptureCurrentPage,
  runDemoCaptureTour,
  type CaptureTourProgress,
  type DemoCaptureMode,
} from "./run-demo-capture-tour";
import { DEMO_CAPTURE_PROFILES } from "./demo-capture.config";
import { MAGAZINE_TOUR_STEPS, countMatrixTourSteps, tourMetaForMode } from "./demo-capture.tours";
import { buildPageSlugFromLocation } from "./demo-capture-current";

const MODE_ORDER: DemoCaptureMode[] = ["magazine", "full", "matrix", "current"];

function countForMode(mode: DemoCaptureMode): number {
  if (mode === "current") return 1;
  if (mode === "magazine") return MAGAZINE_TOUR_STEPS.length;
  if (mode === "matrix") return countMatrixTourSteps(DEMO_CAPTURE_PROFILES);
  return DEMO_CAPTURE_PROFILES.reduce((n, p) => n + p.pages.length, 0);
}

function filenameHintForMode(mode: DemoCaptureMode): string {
  if (mode === "magazine") return "magazine-NN-profil-page.png";
  if (mode === "current") return "profil__url-slug.png";
  if (mode === "matrix") return "matrix/profil__page.png";
  return "profil__page.png";
}

function badgeForMode(mode: DemoCaptureMode): string | null {
  if (mode === "magazine") return "Recommandé";
  if (mode === "full") return "Complet";
  if (mode === "matrix") return "Exhaustif";
  return "Rapide";
}

function idleProgress(mode: DemoCaptureMode): CaptureTourProgress {
  return {
    phase: "idle",
    mode,
    current: 0,
    total: countForMode(mode),
    saved: [],
    errors: [],
  };
}

export function DemoCaptureOverlay() {
  const enabled = isDemoCaptureEnabled();
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [panelOpen, setPanelOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [mode, setMode] = useState<DemoCaptureMode>("magazine");
  const [progress, setProgress] = useState(() => idleProgress("magazine"));

  const tourMeta = useMemo(() => tourMetaForMode(mode), [mode]);
  const currentPageSlug = useMemo(
    () => buildPageSlugFromLocation(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const startTour = useCallback(
    async (selectedMode: DemoCaptureMode) => {
      if (running) return;
      setMode(selectedMode);
      setRunning(true);
      setPanelOpen(true);
      setProgress(idleProgress(selectedMode));

      if (selectedMode === "current") {
        await runCaptureCurrentPage({
          pathname: location.pathname,
          search: location.search,
          user,
          onProgress: setProgress,
          pickDirectory: pickCaptureDirectory,
        });
      } else {
        await runDemoCaptureTour({
          mode: selectedMode,
          onProgress: setProgress,
          navigate: (path) => navigate(path),
          login: async (credentials) => {
            await login(credentials);
          },
          pickDirectory: pickCaptureDirectory,
        });
      }

      setRunning(false);
    },
    [location.pathname, location.search, login, navigate, running, user],
  );

  if (!enabled || !isAuthenticated) return null;

  const pct =
    progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  const doneMessage =
    progress.mode === "magazine"
      ? "Importez dans Canva — présentation marketing RDC. Voir docs/SCRIPT-VOIX-OFF-MARKETING-RDC.md"
      : progress.mode === "matrix"
        ? "Matrice exportée — chaque profil sur chaque page. Dossier matrix/ recommandé."
      : progress.mode === "current"
        ? "Fichier prêt pour Canva. Naviguez ailleurs puis relancez pour une autre page."
        : "Importez dans Canva (présentation, réseaux). Voir assets/canva/captures/README.md";

  const activeProgressMeta = tourMetaForMode(progress.mode);

  return (
    <>
      <div className="demo-capture-ui demo-capture-fab" data-demo-capture-hide="true">
        {!panelOpen && !running ? (
          <button
            type="button"
            className="btn btn-warning btn-sm demo-capture-fab__btn"
            title="Captures marketing pour Canva"
            onClick={() => setPanelOpen(true)}
          >
            Captures Canva
          </button>
        ) : null}
      </div>

      {panelOpen ? (
        <div
          className="demo-capture-ui demo-capture-panel"
          role="dialog"
          aria-labelledby="demo-capture-title"
          data-demo-capture-hide="true"
        >
          <div className="demo-capture-panel__card">
            <div className="demo-capture-panel__header">
              <h2 id="demo-capture-title" className="h6 mb-0">
                Captures marketing
              </h2>
              {!running ? (
                <button
                  type="button"
                  className="btn-close btn-close-sm"
                  aria-label="Fermer"
                  onClick={() => setPanelOpen(false)}
                />
              ) : null}
            </div>

            {!running && progress.phase === "idle" ? (
              <div className="demo-capture-mode-picker mb-3">
                <p className="small text-muted mb-2">Choisissez le parcours à scanner :</p>
                {MODE_ORDER.map((key) => {
                  const tour = tourMetaForMode(key);
                  const count = countForMode(key);
                  const badge = badgeForMode(key);
                  const isCurrent = key === "current";
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`demo-capture-mode-card${mode === key ? " demo-capture-mode-card--active" : ""}`}
                      onClick={() => setMode(key)}
                    >
                      {badge ? (
                        <span className="demo-capture-mode-card__badge">{badge}</span>
                      ) : null}
                      <strong>{tour.label}</strong>
                      <span className="small text-muted d-block mt-1">{tour.description}</span>
                      {isCurrent ? (
                        <span className="small d-block mt-1">
                          Page actuelle : <code>{currentPageSlug}</code>
                        </span>
                      ) : null}
                      <span className="small">
                        <strong>{count}</strong> capture{count > 1 ? "s" : ""} ·{" "}
                        <code>{filenameHintForMode(key)}</code>
                      </span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="btn btn-warning w-100 mt-2"
                  onClick={() => void startTour(mode)}
                >
                  {mode === "current" ? "Capturer cette page" : `Lancer — ${tourMeta.label}`}
                </button>
              </div>
            ) : null}

            {running || progress.phase !== "idle" ? (
              <>
                <p className="small mb-2">
                  <strong>{activeProgressMeta.label}</strong>
                  {progress.magazineSection ? (
                    <span className="text-muted"> · {progress.magazineSection}</span>
                  ) : null}
                </p>

                <div className="progress mb-2" style={{ height: 8 }}>
                  <div
                    className="progress-bar bg-warning"
                    role="progressbar"
                    style={{ width: `${pct}%` }}
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>

                <div className="demo-capture-panel__status small">
                  {progress.phase === "login" && progress.profileLabel ? (
                    <span>Connexion : {progress.profileLabel}</span>
                  ) : null}
                  {progress.phase === "navigate" && progress.pageLabel ? (
                    <span>Navigation : {progress.pageLabel}</span>
                  ) : null}
                  {progress.phase === "wait" ? <span>Chargement étendu…</span> : null}
                  {progress.phase === "capture" && progress.pageLabel ? (
                    <span>Capture : {progress.pageLabel}</span>
                  ) : null}
                  {progress.phase === "done" ? (
                    <span className="text-success">
                      Terminé — {progress.saved.length} capture(s).
                    </span>
                  ) : null}
                  {progress.phase === "error" ? (
                    <span className="text-danger">Erreur pendant le parcours.</span>
                  ) : null}
                  {running ? (
                    <span className="text-muted d-block mt-1">
                      {progress.current}/{progress.total} — ne fermez pas l&apos;onglet.
                    </span>
                  ) : null}
                </div>

                {progress.saved.length > 0 ? (
                  <ul className="demo-capture-panel__list small mb-2">
                    {progress.saved.slice(-5).map((name) => (
                      <li key={name}>
                        <code>{name}</code>
                      </li>
                    ))}
                    {progress.saved.length > 5 ? (
                      <li className="text-muted">… +{progress.saved.length - 5} autres</li>
                    ) : null}
                  </ul>
                ) : null}

                {progress.errors.length > 0 ? (
                  <div className="alert alert-warning py-2 small mb-2">
                    {progress.errors.map((err) => (
                      <div key={err}>{err}</div>
                    ))}
                  </div>
                ) : null}

                {!running && progress.phase === "done" ? (
                  <p className="small mb-2 text-muted">{doneMessage}</p>
                ) : null}

                {!running && progress.phase === "done" ? (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm w-100"
                    onClick={() => {
                      setProgress(idleProgress(mode));
                    }}
                  >
                    Nouvelle capture
                  </button>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
